import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { setAuthToken } from '../lib/api';
import AriEmblem from '../components/AriEmblem';
import ChatbotWidget from '../components/ChatbotWidget';
import EmailVerification from '../components/EmailVerification';
import BookDemoModal from '../components/BookDemoModal';
import CountUp from '../components/CountUp';
import { 
  ArrowRight, Check, ChevronRight, BarChart3, Users, Globe2, 
  Zap, Heart, Shield, TrendingUp, Building2, Award, 
  Eye, EyeOff, Play, Star, Sparkles, Target, PieChart,
  ArrowUpRight, Menu, X, CalendarDays, Volume2, VolumeX
} from 'lucide-react';

const QUICK_LOGIN_PASSWORD = process.env.REACT_APP_QUICK_LOGIN_PASSWORD || '';
// Shortcut logins only appear on preview/dev hosts, or explicitly via ?dev=1 — never on a live domain by accident
const showQuickLogins = typeof window !== 'undefined' && (
  /(^|\.)preview\.emergentagent\.com$|^localhost$|^127\.0\.0\.1$/.test(window.location.hostname) ||
  new URLSearchParams(window.location.search).get('dev') === '1'
);
const QUICK_LOGINS = process.env.REACT_APP_DEMO_LOGINS === 'true' && QUICK_LOGIN_PASSWORD ? [
  { id: 'business-admin', label: 'Business Admin — New Jersey Resources', email: 'hbajwa@njresources.com',
    password: QUICK_LOGIN_PASSWORD, icon: Building2, tone: 'bg-blue-600' },
  { id: 'demo-company', label: 'Demo Company — Horizon Technologies', email: 'demo@horizon-tech.com',
    password: QUICK_LOGIN_PASSWORD, icon: Sparkles, tone: 'bg-indigo-600' },
  { id: 'employee', label: 'Employee Portal — Sarah Mitchell', email: 'sarah.mitchell@horizon-tech.com',
    password: QUICK_LOGIN_PASSWORD, icon: Users, tone: 'bg-emerald-600' },
] : [];

const TOUR_CHAPTERS = [
  { at: 0, stamp: '00:00', title: 'Impact dashboard' },
  { at: 8, stamp: '00:08', title: 'Charity discovery' },
  { at: 13, stamp: '00:13', title: 'Campaigns' },
  { at: 17, stamp: '00:17', title: 'Donations & payouts' },
  { at: 22, stamp: '00:22', title: 'Employer matching' },
  { at: 25, stamp: '00:25', title: 'Payroll giving' },
  { at: 28, stamp: '00:28', title: 'AI marketing studio' },
  { at: 41, stamp: '00:41', title: 'Site builder' },
  { at: 54, stamp: '00:54', title: 'Your published site' },
  { at: 62, stamp: '01:02', title: 'Board reports' },
];

const EXAMPLE_SITES = [
  { label: 'Horizon Technologies', href: '/impact/biz_demo_horizon' },
  { label: 'A campaign page', href: '/impact/biz_demo_horizon/campaign/camp_demo_1' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [formData, setFormData] = useState({
    email: '', password: '', name: '', role: 'business_admin',
    company_name: '', industry: '', company_size: '',
    headquarters_city: '', headquarters_state: '', company_website: '',
    job_title: '', phone: ''
  });
  const [signupStep, setSignupStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [platformStats, setPlatformStats] = useState(null);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationEmailFailed, setVerificationEmailFailed] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1=enter email, 2=enter code+new password
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [tourTime, setTourTime] = useState(0);
  const [tourMuted, setTourMuted] = useState(true);
  const [soundBlocked, setSoundBlocked] = useState(false);
  const tourSectionRef = useRef(null);
  const tourInViewRef = useRef(false);
  const videoRef = useRef(null);
  const [payoutProof, setPayoutProof] = useState(null);

  useEffect(() => {
    // Fetch live platform stats for social proof
    api.get('/api/platform/stats').then(r => setPlatformStats(r.data)).catch(() => {});
    api.get('/api/donations/payout-proof', { params: { limit: 6 } }).then(r => setPayoutProof(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    // Staggered scroll reveals for landing sections
    const nodes = Array.from(document.querySelectorAll('.reveal'));
    nodes.forEach((n, i) => n.style.setProperty('--reveal-delay', `${(i % 6) * 70}ms`));
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });
    nodes.forEach(n => observer.observe(n));
    return () => observer.disconnect();
  }, [payoutProof, platformStats]);

  useEffect(() => {
    // Ari starts talking when the tour scrolls into view, and goes quiet when it leaves.
    // Browsers only allow unmuted playback once the visitor has interacted with the page,
    // so a blocked attempt falls back to the sound button (and retries on first click).
    const section = tourSectionRef.current;
    const video = videoRef.current;
    if (!section || !video) return;

    const unmute = () => {
      video.muted = false;
      video.volume = 1;
      const started = video.play();
      Promise.resolve(started)
        .then(() => {
          setTourMuted(false);
          setSoundBlocked(false);
        })
        .catch(() => {
          video.muted = true;
          video.play().catch(() => {});
          setTourMuted(true);
          setSoundBlocked(true);
        });
    };

    const observer = new IntersectionObserver(([entry]) => {
      tourInViewRef.current = entry.isIntersecting && entry.intersectionRatio >= 0.45;
      if (tourInViewRef.current) {
        if (video.muted) unmute();
      } else if (!video.muted) {
        video.muted = true;
        setTourMuted(true);
      }
    }, { threshold: [0, 0.45, 0.8] });
    observer.observe(section);

    const retryOnGesture = () => {
      if (tourInViewRef.current && video.muted) unmute();
    };
    window.addEventListener('pointerdown', retryOnGesture);
    window.addEventListener('keydown', retryOnGesture);

    return () => {
      observer.disconnect();
      window.removeEventListener('pointerdown', retryOnGesture);
      window.removeEventListener('keydown', retryOnGesture);
    };
  }, []);


  const INDUSTRIES = [
    'Technology', 'Healthcare', 'Finance & Banking', 'Retail & E-commerce', 
    'Manufacturing', 'Education', 'Energy & Utilities', 'Real Estate',
    'Media & Entertainment', 'Professional Services', 'Non-profit', 'Other'
  ];

  const COMPANY_SIZES = [
    { value: '1-50', label: '1-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '201-1000', label: '201-1,000 employees' },
    { value: '1001-5000', label: '1,001-5,000 employees' },
    { value: '5001+', label: '5,001+ employees' }
  ];

  const PERSONAL_DOMAINS = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com', 'mail.com', 'protonmail.com'];
  const isCompanyEmail = (email) => {
    const domain = email.split('@')[1]?.toLowerCase();
    return domain && !PERSONAL_DOMAINS.includes(domain);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    if (authMode === 'signup' && !isCompanyEmail(formData.email)) {
      setError('Please use your company email. Personal emails (Gmail, Yahoo, etc.) are not allowed.');
      return;
    }
    setLoading(true);
    try {
      const endpoint = authMode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const payload = authMode === 'login' 
        ? { email: formData.email, password: formData.password }
        : {
            email: formData.email,
            password: formData.password,
            name: formData.name,
            role: formData.role,
            company_name: formData.company_name,
            industry: formData.industry,
            company_size: formData.company_size,
            headquarters_city: formData.headquarters_city,
            headquarters_state: formData.headquarters_state,
            company_website: formData.company_website,
            job_title: formData.job_title,
            phone: formData.phone,
          };
      const response = await api.post(endpoint, payload);
      const user = response.data.user;
      if (response.data.session_token) {
        setAuthToken(response.data.session_token);
      }

      // After signup: show email verification screen
      if (authMode === 'signup' && !user.email_verified) {
        setVerificationEmail(user.email);
        setVerificationEmailFailed(response.data.verification_email_sent === false);
        setShowVerification(true);
        setShowAuthModal(false);
        return;
      }

      // Login or verified: redirect
      if (user.role === 'super_admin') navigate('/admin');
      else if (user.role === 'business_admin') navigate('/dashboard');
      else if (user.role === 'view_only_admin') navigate('/analytics');
      else if (user.role === 'employee') {
        if (user.business_id) navigate(`/impact/${user.business_id}`);
        else setError('No business associated with your account');
      } else navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.needs_verification) {
        setVerificationEmail(detail.email || formData.email);
        setVerificationEmailFailed(false);
        setShowVerification(true);
        setShowAuthModal(false);
        try { await api.post('/api/auth/resend-verification', { email: detail.email || formData.email }); } catch (e) { /* the screen has its own resend */ }
        return;
      }
      setError(typeof detail === 'string' ? detail : detail?.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = async (email, password) => {
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/api/auth/login', { email, password });
      const user = response.data.user;
      if (response.data.session_token) setAuthToken(response.data.session_token);
      if (user.role === 'super_admin') navigate('/admin');
      else if (user.role === 'view_only_admin') navigate('/analytics');
      else if (user.role === 'employee') navigate(`/impact/${user.business_id}`);
      else navigate('/dashboard');
    } catch (error) {
      setError(error.response?.data?.detail || 'Quick login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setForgotSuccess('');
    setLoading(true);
    try {
      if (forgotStep === 1) {
        await api.post('/api/auth/forgot-password', { email: forgotEmail });
        setForgotStep(2);
        setForgotSuccess('If an account exists with that email, a reset code has been sent.');
      } else {
        await api.post('/api/auth/reset-password', { email: forgotEmail, code: resetCode, new_password: newPassword });
        setForgotSuccess('Password reset successfully! You can now log in.');
        setTimeout(() => {
          setForgotPasswordMode(false);
          setForgotStep(1);
          setForgotEmail('');
          setResetCode('');
          setNewPassword('');
          setForgotSuccess('');
          setAuthMode('login');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show email verification screen after signup
  if (showVerification) {
    return (
      <EmailVerification
        email={verificationEmail}
        deliveryFailed={verificationEmailFailed}
        onVerified={() => {
          setShowVerification(false);
          navigate('/dashboard');
        }}
        onBack={() => {
          setShowVerification(false);
          setAuthMode('signup');
          setShowAuthModal(true);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen alt-landing" style={{ fontFamily: "'Montserrat', 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif", background: '#07090E' }}>
      
      {/* ═══════════════ HEADER ═══════════════ */}
      <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${scrolled ? 'bg-[#07090E]/90 backdrop-blur-xl border-b border-slate-800/60' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2.5">
              <img src="/logo-mark.png" alt="Altrunic" className="w-9 h-9 object-contain" />
              <span className="text-lg font-bold tracking-tight text-slate-50">Altrunic</span>
            </div>

            <nav className="hidden lg:flex items-center gap-8">
              {['Features', 'Demo', 'Pricing'].map(item => (
                <a key={item} href={`#${item.toLowerCase()}`} className="text-sm font-medium text-slate-400 hover:text-slate-100 transition-colors duration-200">{item}</a>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <button
                data-testid="header-book-demo-btn"
                onClick={() => setShowDemoModal(true)}
                className="text-sm font-semibold text-slate-400 hover:text-slate-100 px-3 py-2 transition-colors"
              >
                Book a demo
              </button>
              <button
                data-testid="header-login-btn"
                onClick={() => { setAuthMode('login'); setShowAuthModal(true); }}
                className="text-sm font-semibold text-slate-300 hover:text-white px-4 py-2 transition-colors"
              >
                Log in
              </button>
              <button
                data-testid="header-get-started-btn"
                onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }}
                className="text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 px-5 py-2.5 rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-600/20"
              >
                Get started free
              </button>
            </div>

            <button className="lg:hidden p-2 text-slate-400" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-slate-900/95 backdrop-blur-xl border-t border-slate-800">
            <div className="px-6 py-4 space-y-3">
              {['Features', 'Demo', 'Pricing'].map(item => (
                <a key={item} href={`#${item.toLowerCase()}`} onClick={() => setMobileMenuOpen(false)} className="block text-sm font-medium text-slate-400 py-2">{item}</a>
              ))}
              <div className="pt-3 border-t border-slate-800 flex flex-col gap-2">
                <button onClick={() => { setShowDemoModal(true); setMobileMenuOpen(false); }} className="text-sm font-semibold text-slate-400 py-2 text-left" data-testid="mobile-book-demo-btn">Book a demo</button>
                <button onClick={() => { setAuthMode('login'); setShowAuthModal(true); setMobileMenuOpen(false); }} className="text-sm font-semibold text-slate-300 py-2">Log in</button>
                <button onClick={() => { setAuthMode('signup'); setShowAuthModal(true); setMobileMenuOpen(false); }} className="text-sm font-semibold text-white bg-blue-600 py-2.5 rounded-lg">Get started free</button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ═══════════════ HERO ═══════════════ */}
      <section className="relative pt-24 pb-6 lg:pt-28 lg:pb-12 overflow-hidden alt-grain">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950/20 via-transparent to-transparent" />
        <div className="absolute top-20 -left-40 w-[600px] h-[600px] bg-blue-600/[0.07] rounded-full blur-[120px] alt-float" />
        <div className="absolute bottom-0 -right-40 w-[500px] h-[500px] bg-indigo-600/[0.07] rounded-full blur-[120px] alt-float-slow" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8">
            
            {/* LEFT — Text + Ari speech bubble */}
            <div className="flex-1 text-center lg:text-left">
              {/* Category badge */}
              <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-slate-900/70 backdrop-blur-xl border border-slate-700/60 mb-6" data-testid="ari-badge">
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-75" />
                  <span className="relative w-1.5 h-1.5 rounded-full bg-blue-400" />
                </span>
                <span className="text-[11px] font-semibold text-slate-300 tracking-[0.18em] uppercase font-mono">Workplace giving &amp; CSR platform</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-50 leading-[1.08]">
                Run your company's
                <br />
                <span className="bg-gradient-to-r from-blue-400 via-indigo-300 to-blue-400 bg-clip-text text-transparent alt-shimmer-text">
                  giving program in one place
                </span>
              </h1>

              <p className="mt-5 text-base md:text-lg text-slate-400 leading-relaxed max-w-xl mx-auto lg:mx-0" data-testid="hero-subheadline">
                Altrunic is the software companies use to launch employee donation campaigns,
                automatically match what their team gives, and report the real-world impact —
                across <strong className="text-slate-200 font-semibold">1.8M+ verified US charities</strong>,
                with every dollar tracked from payroll to payout.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
                <button
                  data-testid="hero-get-started-btn"
                  onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }}
                  className="group alt-shine flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold px-7 py-3.5 rounded-xl transition-all duration-200 hover:shadow-xl hover:shadow-blue-600/20 text-sm w-full sm:w-auto justify-center"
                >
                  Start your giving program
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
                <a
                  href="#how-it-works"
                  className="group flex items-center gap-2 text-slate-300 hover:text-white font-semibold px-7 py-3.5 rounded-xl border border-slate-700 hover:border-slate-500 bg-slate-900/50 backdrop-blur transition-all duration-200 text-sm w-full sm:w-auto justify-center"
                >
                  <Play className="w-4 h-4" />
                  See how it works
                </a>
              </div>

              <button
                onClick={() => setShowDemoModal(true)}
                className="mt-3 text-sm font-semibold text-blue-400 hover:text-blue-300 transition-colors"
                data-testid="hero-book-demo-link"
              >
                Or book a live demo with our team →
              </button>

              <div className="mt-6 flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-2 text-xs text-slate-500">
                <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-blue-400" /> Free forever plan</span>
                <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-blue-400" /> 5 min setup</span>
                <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-blue-400" /> 0% platform fee</span>
              </div>
            </div>
            
            {/* RIGHT — Ari emblem */}
            <div className="flex-shrink-0 flex items-center justify-center relative w-full max-w-[440px] lg:w-[440px] lg:self-start lg:-mt-6" data-testid="hero-ari-wrap">
              <AriEmblem />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ WHAT ALTRUNIC DOES ═══════════════ */}
      <section className="border-y border-slate-800/60 bg-slate-950/40" data-testid="what-it-does-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 grid sm:grid-cols-3 gap-8">
          {[
            { icon: Building2, title: 'For HR & CSR teams', desc: 'Launch campaigns, set matching budgets and export board-ready impact reports.' },
            { icon: Users, title: 'For employees', desc: 'Give to any verified US charity from work — and get your donation matched.' },
            { icon: Shield, title: 'For every dollar', desc: 'Donations are processed and receipted by Every.org, then granted to the charity — 0% platform fee from us.' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3.5 reveal" data-testid={`what-it-does-${i}`}>
              <div className="w-9 h-9 rounded-lg bg-blue-600/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                <item.icon className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-100">{item.title}</p>
                <p className="text-sm text-slate-500 leading-relaxed mt-1">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>


      {/* ═══════════════ PRODUCT WALKTHROUGH VIDEO ═══════════════ */}
      <section id="demo" ref={tourSectionRef} className="py-12 relative overflow-hidden border-y border-slate-800/60 alt-grain scroll-mt-16" style={{ background: '#0B0F1A' }} data-testid="product-screenshot-section">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue-600/[0.06] rounded-full blur-[120px] alt-float" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="max-w-3xl reveal">
            <span className="text-xs font-semibold text-blue-400 tracking-[0.2em] uppercase font-mono">See it in motion</span>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">
              A 70-second tour of the platform your CSR team actually works in
            </h2>
            <p className="mt-2.5 text-sm md:text-base text-slate-400 leading-relaxed">
              Recorded straight from the live product and narrated by Ari — giving, matching, payroll,
              the AI marketing studio, the site builder and board-ready reports.
            </p>
          </div>

          <div className="mt-6 grid lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-8 relative group reveal">
              <div className="absolute -inset-px bg-gradient-to-r from-blue-600/30 via-indigo-500/20 to-transparent rounded-2xl blur-sm opacity-60" />
              <div className="relative rounded-2xl overflow-hidden border border-slate-700/60 bg-slate-900 shadow-[0_32px_90px_rgba(0,0,0,0.6)]">
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-800 bg-slate-900/90">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
                  <span className="ml-3 text-xs text-slate-500 font-mono hidden sm:inline">altrunic.com/dashboard</span>
                  <button
                    onClick={() => {
                      const v = videoRef.current;
                      if (!v) return;
                      v.muted = !v.muted;
                      setTourMuted(v.muted);
                      setSoundBlocked(false);
                      v.play().catch(() => {});
                    }}
                    className={`ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-colors duration-200 ${
                      tourMuted && soundBlocked
                        ? 'border-blue-500/70 bg-blue-600/15 text-blue-200 animate-pulse'
                        : 'border-slate-700 hover:border-blue-500/60 text-slate-300 hover:text-white'
                    }`}
                    data-testid="tour-sound-btn"
                  >
                    {tourMuted
                      ? <><VolumeX className="w-3.5 h-3.5" /> Hear Ari</>
                      : <><Volume2 className="w-3.5 h-3.5 text-blue-400" /> Sound on</>}
                  </button>
                </div>
                <video
                  ref={videoRef}
                  poster="/videos/platform-tour-poster.jpg"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  onTimeUpdate={(e) => setTourTime(e.target.currentTime)}
                  className="w-full block bg-slate-950"
                  aria-label="Narrated seventy second tour of the Altrunic platform — dashboard, charities, campaigns, donations, matching, payroll giving, AI marketing studio, site builder and reports"
                  data-testid="dashboard-walkthrough-video"
                >
                  <source src="/videos/platform-tour.webm" type="video/webm" />
                  <source src="/videos/platform-tour.mp4" type="video/mp4" />
                </video>
              </div>
            </div>

            <div className="lg:col-span-4 reveal">
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-2.5">Chapters · jump anywhere</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-1 gap-1.5">
                {TOUR_CHAPTERS.map((c, i) => {
                  const next = TOUR_CHAPTERS[i + 1];
                  const active = tourTime >= c.at && (!next || tourTime < next.at);
                  return (
                    <button
                      key={c.at}
                      onClick={() => {
                        const v = videoRef.current;
                        if (v) { v.currentTime = c.at; v.play().catch(() => {}); }
                      }}
                      className={`flex items-center gap-3 text-left px-3 py-1.5 rounded-lg border transition-all duration-200 ${
                        active
                          ? 'bg-blue-600/10 border-blue-500/50'
                          : 'bg-slate-900/40 border-slate-800/80 hover:border-slate-600'
                      }`}
                      data-testid={`tour-chapter-${i}`}
                    >
                      <span className={`text-[11px] font-mono font-bold w-9 flex-shrink-0 ${active ? 'text-blue-300' : 'text-slate-500'}`}>{c.stamp}</span>
                      <span className={`text-sm font-semibold truncate ${active ? 'text-slate-50' : 'text-slate-300'}`}>{c.title}</span>
                      {active && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setShowDemoModal(true)}
                className="mt-4 w-full alt-shine flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold px-5 py-3 rounded-xl text-sm transition-all duration-200 hover:shadow-xl hover:shadow-blue-600/20"
                data-testid="walkthrough-demo-btn"
              >
                <CalendarDays className="w-4 h-4" />
                Get a guided walkthrough
              </button>

              <div className="mt-2.5 rounded-xl border border-slate-800/80 bg-slate-900/40 px-3.5 py-2.5" data-testid="example-sites">
                <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
                  Sites built with the builder
                </p>
                <div className="mt-1.5 flex flex-col gap-1">
                  {EXAMPLE_SITES.map((site, i) => (
                    <a
                      key={site.href}
                      href={site.href}
                      target="_blank"
                      rel="noreferrer"
                      className="group flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-blue-300 transition-colors duration-200"
                      data-testid={`example-site-${i}`}
                    >
                      <Globe2 className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                      {site.label}
                      <ArrowUpRight className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ TRUSTED BY (PLACEHOLDER MARKS) ═══════════════ */}
      <section className="py-14" style={{ background: '#07090E' }} data-testid="trusted-by-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-center text-xs font-semibold text-slate-500 tracking-[0.2em] uppercase font-mono reveal">
            Built for corporate giving teams
          </p>
        </div>
        <div className="mt-8 alt-marquee-mask overflow-hidden">
          <div className="alt-marquee gap-4">
            {[0, 1].map(pass => (
              <div key={pass} className="flex gap-4 pr-4" aria-hidden={pass === 1}>
                {[
                  { mark: 'NV', name: 'Northvale' },
                  { mark: 'HZ', name: 'Horizon Tech' },
                  { mark: 'MR', name: 'Meridian Co' },
                  { mark: 'AX', name: 'Axiom Labs' },
                  { mark: 'CV', name: 'Civic Bank' },
                  { mark: 'LM', name: 'Lumen Group' },
                  { mark: 'QS', name: 'Quaystone' },
                  { mark: 'BR', name: 'Bridgerow' },
                ].map((c, i) => (
                  <div
                    key={i}
                    className="alt-lift flex items-center justify-center gap-2.5 py-5 px-6 w-[220px] flex-shrink-0 rounded-xl border border-slate-800/70 bg-slate-900/40 hover:border-blue-500/30"
                    data-testid={pass === 0 ? `trusted-logo-${i}` : undefined}
                  >
                    <span className="w-8 h-8 rounded-md bg-slate-800 text-slate-300 text-[11px] font-extrabold tracking-tight flex items-center justify-center">{c.mark}</span>
                    <span className="text-sm font-semibold text-slate-400 tracking-tight whitespace-nowrap">{c.name}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="mt-6 text-center text-xs text-slate-600" data-testid="trusted-by-disclaimer">
            Illustrative company marks — we're onboarding our first cohort now. Want your logo here?{' '}
            <button onClick={() => setShowDemoModal(true)} className="text-blue-400 hover:text-blue-300 font-semibold underline decoration-blue-500/40 underline-offset-2" data-testid="trusted-by-demo-link">
              Book a demo
            </button>
          </p>
        </div>
      </section>

      {/* ═══════════════ YOUR DOLLAR'S JOURNEY ═══════════════ */}
      <section id="how-it-works" className="py-24 relative overflow-hidden" style={{ background: '#07090E' }}>
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950/10 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="text-center max-w-2xl mx-auto mb-16 reveal">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/80 backdrop-blur-xl border border-slate-700/60 mb-6">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/40cc5c7f-c45c-491d-ad3f-cc7864e78796/images/4063fc24bf32f027848a8288e2bda515ed9b161b95609e2e01d5ded18984e549.jpeg"
                alt="Ari" className="alt-screen w-5 h-5 rounded-full object-cover"
              />
              <span className="text-xs font-semibold text-blue-400 tracking-[0.15em] uppercase font-mono">Ari explains</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100">Watch your $100 become $200 of real impact</h2>
            <p className="mt-4 text-base text-slate-400 leading-relaxed">When you donate through Altrunic, employer matching doubles your gift automatically. Zero hidden fees.</p>
          </div>

          <div className="grid md:grid-cols-4 gap-4 max-w-4xl mx-auto" data-testid="dollar-journey">
            {[
              { icon: Heart, label: 'You Give', amount: '$100', desc: 'Donate in-app to any verified 501(c)(3) charity', gradient: 'from-blue-500 to-blue-600' },
              { icon: Users, label: 'Employer Matches', amount: '+$100', desc: 'Your company automatically matches your donation', gradient: 'from-indigo-500 to-indigo-600' },
              { icon: Shield, label: 'Secure Processing', amount: '$200', desc: 'Stripe processes payment securely, zero platform fees', gradient: 'from-sky-500 to-cyan-600' },
              { icon: Building2, label: 'Charity Receives', amount: '$200', desc: 'Full amount disbursed directly to the nonprofit', gradient: 'from-violet-500 to-purple-600' },
            ].map((item, i) => (
              <div key={i} className="relative bg-slate-900/80 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 text-center hover:border-blue-500/40 hover:shadow-xl hover:shadow-blue-950/20 alt-lift reveal group" data-testid={`dollar-step-${i}`}>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300`}>
                  <item.icon className="w-5 h-5 text-white" />
                </div>
                <p className="text-2xl font-extrabold text-slate-50">{item.amount}</p>
                <p className="text-sm font-bold text-slate-200 mt-1">{item.label}</p>
                <p className="text-xs text-slate-500 mt-2 leading-relaxed">{item.desc}</p>
                {i < 3 && <div className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 z-10"><ChevronRight className="w-5 h-5 text-slate-600" /></div>}
              </div>
            ))}
          </div>

          <div className="mt-10 max-w-2xl mx-auto flex items-start gap-4 bg-slate-900/60 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-5" data-testid="ari-dollar-callout">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/40cc5c7f-c45c-491d-ad3f-cc7864e78796/images/4063fc24bf32f027848a8288e2bda515ed9b161b95609e2e01d5ded18984e549.jpeg"
              alt="Ari" className="alt-screen w-10 h-10 rounded-full object-cover ring-2 ring-blue-500/30 flex-shrink-0"
            />
            <div>
              <p className="text-sm text-slate-300 leading-relaxed">
                <strong className="text-blue-400">Did you know?</strong> Over <strong className="text-slate-100">$4 billion</strong> in employer matching funds go unclaimed every year. Altrunic makes claiming automatic — your company matches, your impact doubles, no paperwork needed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ STATS RIBBON ═══════════════ */}
      <section className="py-14 relative overflow-hidden border-y border-slate-800/60" style={{ background: '#0B0F1A' }}>
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '32px 32px'}} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {[
              { value: 1.8, suffix: 'M+', decimals: 1, label: 'Verified charities', sub: 'ProPublica data' },
              { value: 35, prefix: '$', suffix: 'T', decimals: 0, label: 'ESG assets globally', sub: 'Bloomberg Intelligence' },
              { value: 83, suffix: '%', decimals: 0, label: 'Consumers prefer giving brands', sub: 'Nielsen Study' },
              { value: 2.5, suffix: 'x', decimals: 1, label: 'Better employee retention', sub: 'Gallup Research' },
            ].map((item, i) => (
              <div key={i} className="text-center lg:text-left reveal">
                <CountUp
                  value={item.value}
                  prefix={item.prefix}
                  suffix={item.suffix}
                  decimals={item.decimals}
                  className="block text-3xl lg:text-4xl font-extrabold text-slate-50 tracking-tight tabular-nums"
                  testId={`stat-counter-${i}`}
                />
                <div className="text-sm text-slate-300 mt-1">{item.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{item.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ LIVE SOCIAL PROOF ═══════════════ */}
      {platformStats && (platformStats.total_companies > 0 || platformStats.total_donated > 0) && (
        <section className="py-5 border-b border-slate-800/60" style={{ background: '#0A0E18' }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-sm">
              {platformStats.total_companies > 0 && (
                <span className="text-slate-400 font-semibold">
                  <CountUp value={platformStats.total_companies} className="text-lg font-extrabold text-slate-100 tabular-nums" testId="live-stat-companies" /> companies using Altrunic
                </span>
              )}
              {platformStats.total_donated > 0 && (
                <span className="text-slate-400 font-semibold">
                  <CountUp value={platformStats.total_donated} prefix="$" className="text-lg font-extrabold text-slate-100 tabular-nums" testId="live-stat-donated" /> donated so far
                </span>
              )}
              {platformStats.total_campaigns > 0 && (
                <span className="text-slate-400 font-semibold">
                  <CountUp value={platformStats.total_campaigns} className="text-lg font-extrabold text-slate-100 tabular-nums" testId="live-stat-campaigns" /> active campaigns
                </span>
              )}
              {platformStats.total_employees > 0 && (
                <span className="text-slate-400 font-semibold">
                  <CountUp value={platformStats.total_employees} className="text-lg font-extrabold text-slate-100 tabular-nums" testId="live-stat-employees" /> employees giving
                </span>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ═══════════════ PAYOUT PROOF ═══════════════ */}
      {payoutProof?.entries?.length > 0 && (
        <section className="py-24 relative overflow-hidden" style={{ background: '#07090E' }} data-testid="payout-proof-section">
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-600/5 rounded-full blur-[120px]" />
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
              <div className="max-w-2xl reveal">
                <span className="text-xs font-semibold text-blue-400 tracking-[0.2em] uppercase font-mono">Payout proof</span>
                <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100">
                  Every donation is traceable to the charity that got it
                </h2>
                <p className="mt-4 text-base text-slate-400 leading-relaxed">
                  Donors and finance teams both see the same ledger: amount given, employer match added, payment status and
                  disbursement to the nonprofit. Each donation gets a shareable receipt.
                </p>
              </div>
              {payoutProof.summary?.total_delivered > 0 && (
                <div className="flex-shrink-0 bg-slate-900/70 border border-slate-800 rounded-2xl px-6 py-5" data-testid="payout-proof-summary">
                  <CountUp
                    value={payoutProof.summary.total_delivered}
                    prefix="$"
                    className="block text-3xl font-extrabold text-slate-50 tracking-tight tabular-nums"
                    testId="payout-total-counter"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    delivered across <CountUp value={payoutProof.summary.donation_count} className="tabular-nums" testId="payout-count-counter" /> donations
                  </p>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 backdrop-blur-xl overflow-hidden reveal" data-testid="payout-proof-list">
              <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-3.5 border-b border-slate-800 text-[11px] font-semibold text-slate-500 tracking-[0.15em] uppercase font-mono">
                <span className="col-span-3">Donor</span>
                <span className="col-span-4">Charity</span>
                <span className="col-span-2 text-right">Delivered</span>
                <span className="col-span-3 text-right">Status</span>
              </div>
              {payoutProof.entries.map((e, i) => (
                <div
                  key={i}
                  className="grid sm:grid-cols-12 gap-2 sm:gap-4 px-6 py-4 border-b border-slate-800/60 last:border-0 hover:bg-slate-900/60 transition-colors duration-200"
                  data-testid={`payout-proof-row-${i}`}
                >
                  <div className="sm:col-span-3 flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-blue-600/15 border border-blue-500/25 text-[11px] font-bold text-blue-300 flex items-center justify-center flex-shrink-0">
                      {e.donor?.[0] || 'A'}
                    </span>
                    <span className="text-sm font-semibold text-slate-200">{e.donor}</span>
                  </div>
                  <div className="sm:col-span-4 flex items-center text-sm text-slate-400">{e.charity_name}</div>
                  <div className="sm:col-span-2 flex items-center sm:justify-end text-sm">
                    <span className="font-bold text-slate-100">${(e.amount + e.employer_match_amount).toLocaleString()}</span>
                    {e.employer_match_amount > 0 && (
                      <span className="ml-2 text-xs text-blue-400">incl. ${e.employer_match_amount.toLocaleString()} match</span>
                    )}
                  </div>
                  <div className="sm:col-span-3 flex items-center sm:justify-end gap-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                      e.disbursement_status === 'disbursed'
                        ? 'bg-blue-500/10 text-blue-300 border border-blue-500/25'
                        : 'bg-amber-500/10 text-amber-300 border border-amber-500/25'
                    }`}>
                      <Check className="w-3 h-3" />
                      {e.disbursement_status === 'disbursed' ? 'Sent to charity' : 'Processing'}
                    </span>
                    {e.donation_id ? (
                      <a href={`/receipt/${e.donation_id}`} className="text-xs font-semibold text-slate-400 hover:text-blue-300 transition-colors" data-testid={`payout-receipt-link-${i}`}>
                        Receipt →
                      </a>
                    ) : (
                      <span className="text-xs text-slate-600 font-mono" data-testid={`payout-sample-label-${i}`}>sample</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-5 text-xs text-slate-600" data-testid="payout-proof-note">
              {payoutProof.has_real_data
                ? 'Live donations from the Altrunic ledger. Donor names are shortened for privacy; rows marked "sample" are illustrative.'
                : 'Sample entries shown while our first cohort onboards. Once your team starts giving, this feed shows your real donations with shareable receipts.'}
            </p>
          </div>
        </section>
      )}

      {/* ═══════════════ MEET ARI ═══════════════ */}
      <section id="meet-ari" className="py-24 relative overflow-hidden scroll-mt-20" style={{ background: '#07090E' }}>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-600/5 rounded-full blur-[120px]" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left — Ari visual */}
            <div className="flex justify-center lg:justify-start">
              <div className="relative">
                <div className="absolute -inset-6 bg-blue-500/5 rounded-full blur-3xl" />
                <div className="relative bg-slate-900/80 backdrop-blur-xl rounded-3xl p-8 border border-slate-800/80 shadow-[0_8px_32px_rgba(0,0,0,0.4)] max-w-sm">
                  <img 
                    src="https://static.prod-images.emergentagent.com/jobs/40cc5c7f-c45c-491d-ad3f-cc7864e78796/images/e6c356ac68b784ba5d3acaf8a7ef2da33aba1583a1691fe9fa99e2ca930d702a.jpeg"
                    alt="Ari - Your Impact Guide" className="w-48 h-48 object-contain mx-auto alt-screen alt-emblem-mask alt-breathe-slow"
                    data-testid="ari-portrait"
                  />
                  <div className="text-center mt-5">
                    <div className="flex items-center justify-center gap-2">
                      <h3 className="text-xl font-extrabold text-slate-50">Ari</h3>
                      <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                    </div>
                    <p className="text-sm font-semibold text-blue-400">Your Impact & Dollar Guide</p>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                    <div className="bg-blue-950/40 border border-blue-800/30 rounded-xl p-2">
                      <p className="text-lg font-extrabold text-blue-300">1.8M+</p>
                      <p className="text-[10px] text-slate-500 font-semibold">Charities</p>
                    </div>
                    <div className="bg-indigo-950/40 border border-indigo-800/30 rounded-xl p-2">
                      <p className="text-lg font-extrabold text-indigo-300">2x</p>
                      <p className="text-[10px] text-slate-500 font-semibold">Match</p>
                    </div>
                    <div className="bg-violet-950/40 border border-violet-800/30 rounded-xl p-2">
                      <p className="text-lg font-extrabold text-violet-300">$0</p>
                      <p className="text-[10px] text-slate-500 font-semibold">Fees</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right — Story */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/80 backdrop-blur-xl border border-slate-700/60 mb-6">
                <span className="text-xs font-semibold text-blue-400 tracking-[0.15em] uppercase font-mono">Say hello to Ari</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100 leading-tight">
                I make every corporate dollar<br />speak louder.
              </h2>
              <div className="mt-8 space-y-5">
                {[
                  { icon: Heart, text: "I connect your company with 1.8M+ IRS-verified charities that match your CSR goals, ESG priorities, and geography.", iconColor: 'text-blue-400', bg: 'bg-blue-500/10 border border-blue-500/20' },
                  { icon: Zap, text: "I automate employer matching so employees never leave free money on the table. One click — your gift doubles.", iconColor: 'text-indigo-400', bg: 'bg-indigo-500/10 border border-indigo-500/20' },
                  { icon: Shield, text: "I route every donation through Every.org, the 501(c)(3) that receipts the donor and grants the money to the charity — 0% platform fee from us.", iconColor: 'text-sky-400', bg: 'bg-sky-500/10 border border-sky-500/20' },
                  { icon: BarChart3, text: "I track every dollar from your pocket to the charity's bank account. Real-time status: Paid, Processing, Disbursed.", iconColor: 'text-violet-400', bg: 'bg-violet-500/10 border border-violet-500/20' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 group">
                    <div className={`w-10 h-10 rounded-xl ${item.bg} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-200`}>
                      <item.icon className={`w-5 h-5 ${item.iconColor}`} />
                    </div>
                    <p className="text-sm text-slate-400 leading-relaxed pt-2">{item.text}</p>
                  </div>
                ))}
              </div>
              <button
                data-testid="meet-ari-cta"
                onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }}
                className="mt-10 group flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold px-7 py-3.5 rounded-xl transition-all duration-200 text-sm hover:shadow-xl hover:shadow-blue-600/20"
              >
                Let Ari guide you
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ FEATURES ═══════════════ */}
      <section id="features" className="py-24 relative overflow-hidden" style={{ background: '#0B0F1A' }}>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-950/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="text-center max-w-2xl mx-auto mb-16 reveal">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/80 backdrop-blur-xl border border-slate-700/60 mb-6">
              <span className="text-xs font-semibold text-blue-400 tracking-[0.15em] uppercase font-mono">Features</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100">Everything you need to run corporate giving</h2>
            <p className="mt-4 text-base text-slate-400 leading-relaxed">From AI-powered charity matching to real-time ESG reporting. One platform, zero complexity.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Sparkles, title: 'AI Charity Matching', desc: 'Our engine analyzes your ESG goals, industry, and geography to recommend the perfect charity partners.', gradient: 'from-blue-500 to-indigo-500' },
              { icon: BarChart3, title: 'Campaign Management', desc: 'Launch campaigns with employer matching, goal tracking, and real-time progress dashboards.', gradient: 'from-indigo-500 to-violet-500' },
              { icon: Globe2, title: 'Impact Websites', desc: 'Auto-generate branded impact portals with AI. Showcase your giving story to the world.', gradient: 'from-cyan-500 to-blue-500' },
              { icon: PieChart, title: 'ESG Reporting', desc: 'Track scores, generate compliance reports, and export tax documentation in one click.', gradient: 'from-blue-400 to-sky-500' },
              { icon: Users, title: 'Employee Portal', desc: 'Gamified experience with badges, leaderboards, and matching gift tracking for every team member.', gradient: 'from-violet-500 to-indigo-500' },
              { icon: Shield, title: 'Budget Controls', desc: 'Set giving budgets by department or cause. Approve matching gifts with configurable policies.', gradient: 'from-indigo-400 to-blue-500' },
            ].map((feature, i) => (
              <div key={i} className="group p-6 rounded-2xl bg-slate-900/80 backdrop-blur-xl border border-slate-800/80 hover:border-blue-500/40 hover:shadow-xl hover:shadow-blue-950/20 alt-lift reveal">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-base font-bold text-slate-100 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ HOW IT WORKS (ARI GUIDED) ═══════════════ */}
      <section className="py-24 relative overflow-hidden" style={{ background: '#07090E' }}>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-600/5 rounded-full blur-[120px]" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="text-center max-w-2xl mx-auto mb-16 reveal">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/80 backdrop-blur-xl border border-slate-700/60 mb-6">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/40cc5c7f-c45c-491d-ad3f-cc7864e78796/images/4063fc24bf32f027848a8288e2bda515ed9b161b95609e2e01d5ded18984e549.jpeg"
                alt="Ari" className="alt-screen w-5 h-5 rounded-full object-cover"
              />
              <span className="text-xs font-semibold text-blue-400 tracking-[0.15em] uppercase font-mono">Ari's quick start</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100">Up and running in 3 steps</h2>
            <p className="mt-4 text-base text-slate-400 leading-relaxed">I'll walk you through everything. From account to first donation in under 5 minutes.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
            {[
              { step: '01', title: 'Tell me about your company', desc: 'Sign up with your work email. I\'ll ask about your CSR goals, causes you care about, and where your team is located.', icon: Building2, ariQuote: 'This helps me find charities that perfectly match your mission.' },
              { step: '02', title: 'Discover your perfect charities', desc: 'I\'ll score and rank 1.8M+ charities by your goals, geography, and ESG priorities. Launch a campaign with one click.', icon: Target, ariQuote: 'My AI engine does the research. You pick the winners.' },
              { step: '03', title: 'Donate, match, and track', desc: 'Employees donate in-app. Matching happens automatically. You see every dollar from payment to charity disbursement.', icon: TrendingUp, ariQuote: 'Real-time tracking. No black holes. Every cent accounted for.' },
            ].map((step, i) => (
              <div key={i} className="relative bg-slate-900/80 backdrop-blur-xl rounded-2xl p-8 border border-slate-800/80 hover:border-blue-500/40 hover:shadow-xl hover:shadow-blue-950/20 alt-lift reveal group">
                <div className="text-5xl font-extrabold text-slate-800 mb-4 group-hover:text-blue-900/40 transition-colors font-mono">{step.step}</div>
                <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <step.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-100 mb-2">{step.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-4">{step.desc}</p>
                <div className="flex items-start gap-2 p-3 bg-blue-950/30 border border-blue-800/30 rounded-xl">
                  <img 
                    src="https://static.prod-images.emergentagent.com/jobs/40cc5c7f-c45c-491d-ad3f-cc7864e78796/images/4063fc24bf32f027848a8288e2bda515ed9b161b95609e2e01d5ded18984e549.jpeg"
                    alt="Ari" className="alt-screen w-6 h-6 rounded-full object-cover flex-shrink-0 mt-0.5"
                  />
                  <p className="text-xs text-blue-300 italic leading-relaxed">{step.ariQuote}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ SOLUTIONS ═══════════════ */}
      <section id="solutions" className="py-24 relative overflow-hidden" style={{ background: '#0B0F1A' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="text-center max-w-2xl mx-auto mb-16 reveal">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/80 backdrop-blur-xl border border-slate-700/60 mb-6">
              <span className="text-xs font-semibold text-blue-400 tracking-[0.15em] uppercase font-mono">Solutions</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100">Built for every stakeholder</h2>
            <p className="mt-4 text-base text-slate-400 leading-relaxed">Whether you're a C-suite executive, HR leader, or individual contributor.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { 
                title: 'For Leadership', 
                desc: 'Strategic CSR management with real-time ESG analytics and compliance reporting.',
                features: ['ESG scoring & benchmarks', 'Board-ready reports', 'Tax documentation', 'Budget oversight'],
                gradient: 'from-slate-800 to-slate-900',
                border: 'border-slate-700/60',
              },
              { 
                title: 'For HR Teams', 
                desc: 'Drive engagement and retention through matching gifts and volunteer programs.',
                features: ['Matching gift engine', 'Volunteer hour tracking', 'Team leaderboards', 'HR system integration'],
                gradient: 'from-blue-600 to-indigo-600',
                border: 'border-blue-500/40',
              },
              { 
                title: 'For Employees', 
                desc: 'Discover causes, maximize your impact, and earn recognition for giving back.',
                features: ['Personal giving dashboard', 'Match tracking', 'Badges & rewards', 'Impact certificates'],
                gradient: 'from-indigo-600 to-violet-600',
                border: 'border-indigo-500/40',
              },
            ].map((card, i) => (
              <div key={i} className={`rounded-2xl p-8 bg-gradient-to-br ${card.gradient} border ${card.border} text-white hover:shadow-xl hover:shadow-blue-950/20 alt-lift reveal`}>
                <h3 className="text-xl font-bold mb-3">{card.title}</h3>
                <p className="text-sm text-white/60 mb-6">{card.desc}</p>
                <ul className="space-y-2.5">
                  {card.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2.5 text-sm text-white/70">
                      <Check className="w-4 h-4 flex-shrink-0 text-blue-300" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ═══════════════ PRICING ═══════════════ */}
      <section id="pricing" className="py-24 relative overflow-hidden" style={{ background: '#0B0F1A' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="text-center max-w-2xl mx-auto mb-16 reveal">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/80 backdrop-blur-xl border border-slate-700/60 mb-6">
              <span className="text-xs font-semibold text-blue-400 tracking-[0.15em] uppercase font-mono">Pricing</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100">Simple, transparent pricing</h2>
            <p className="mt-4 text-base text-slate-400 leading-relaxed">Start free. Upgrade when you're ready. We take 0% of every donation.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* STARTER */}
            <div className="rounded-2xl bg-slate-900/80 backdrop-blur-xl border border-slate-800/80 p-8 flex flex-col hover:border-slate-700 alt-lift reveal" data-testid="pricing-tier-free">
              <div className="mb-6">
                <h3 className="text-lg font-bold text-slate-100">Starter</h3>
                <p className="text-sm text-slate-500 mt-1">Get started with corporate giving</p>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-extrabold text-slate-50">$0</span>
                <span className="text-sm text-slate-500">/month</span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {['Up to 25 employees', '1 active campaign', 'Basic impact portal', '3 AI charity matches', 'Basic analytics', '1.8M+ charity access'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-slate-400">
                    <Check className="w-4 h-4 text-blue-400 flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <button
                data-testid="pricing-free-btn"
                onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }}
                className="w-full py-3 rounded-xl font-semibold text-sm border border-slate-700 text-slate-200 hover:bg-slate-800 hover:border-slate-600 transition-all duration-200"
              >
                Get started free
              </button>
            </div>

            {/* PROFESSIONAL */}
            <div className="rounded-2xl bg-slate-900/80 backdrop-blur-xl border-2 border-blue-500/50 p-8 flex flex-col relative shadow-xl shadow-blue-950/20 alt-lift reveal md:-mt-3 md:mb-3" data-testid="pricing-tier-professional">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                MOST POPULAR
              </div>
              <div className="mb-6">
                <h3 className="text-lg font-bold text-slate-100">Professional</h3>
                <p className="text-sm text-slate-500 mt-1">For growing companies</p>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-extrabold text-slate-50">$99</span>
                <span className="text-sm text-slate-500">/month</span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {['Up to 250 employees', 'Unlimited campaigns', 'Custom branded portal', 'Unlimited AI matching', 'Full analytics & reports', 'Employer matching engine', 'ESG scoring', 'Email support'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-slate-400">
                    <Check className="w-4 h-4 text-blue-400 flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <button
                data-testid="pricing-professional-btn"
                onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }}
                className="w-full py-3 rounded-xl font-semibold text-sm bg-blue-600 text-white hover:bg-blue-500 transition-all duration-200 hover:shadow-lg hover:shadow-blue-600/20 alt-shine"
              >
                Start Professional
              </button>
            </div>

            {/* ENTERPRISE */}
            <div className="rounded-2xl bg-slate-900/80 backdrop-blur-xl border border-slate-800/80 p-8 flex flex-col hover:border-slate-700 alt-lift reveal" data-testid="pricing-tier-enterprise">
              <div className="mb-6">
                <h3 className="text-lg font-bold text-slate-100">Enterprise</h3>
                <p className="text-sm text-slate-500 mt-1">Full control & white-label</p>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-extrabold text-slate-50">$299</span>
                <span className="text-sm text-slate-500">/month</span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {['Unlimited employees', 'Unlimited campaigns', 'White-label portal (your domain)', 'Priority AI matching', 'Advanced analytics & exports', 'Employer matching engine', 'Dedicated support', 'API access & SSO'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-slate-400">
                    <Check className="w-4 h-4 text-blue-400 flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <button
                data-testid="pricing-enterprise-btn"
                onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }}
                className="w-full py-3 rounded-xl font-semibold text-sm border border-slate-700 text-slate-200 hover:bg-slate-800 hover:border-slate-600 transition-all duration-200"
              >
                Start Enterprise
              </button>
            </div>
          </div>

          <p className="text-center text-xs text-slate-600 mt-8">All plans include access to 1.8M+ charities. Cancel anytime. No contracts.</p>
        </div>
      </section>

      {/* ═══════════════ TESTIMONIALS ═══════════════ */}
      <section className="py-24 relative overflow-hidden border-t border-slate-800/60" style={{ background: '#07090E' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100">Why companies choose Altrunic</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { quote: 'We went from zero CSR visibility to a fully branded impact portal in one afternoon. Our employees loved it.', name: 'Sarah K.', title: 'VP People & Culture', company: 'Tech Startup, 120 employees' },
              { quote: 'The AI charity matching saved us weeks of research. It recommended perfect nonprofits aligned with our mission.', name: 'Michael R.', title: 'CSR Director', company: 'Manufacturing, 500 employees' },
              { quote: 'At a fraction of the cost of enterprise platforms, we get everything we need. The employer matching feature is a game-changer.', name: 'Lisa T.', title: 'HR Manager', company: 'Financial Services, 80 employees' },
            ].map((t, i) => (
              <div key={i} className="bg-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-slate-800/80 hover:border-blue-500/30 hover:shadow-xl hover:shadow-blue-950/20 alt-lift reveal">
                <div className="flex gap-0.5 mb-4">
                  {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 text-amber-400 fill-amber-400" />)}
                </div>
                <p className="text-sm text-slate-300 leading-relaxed mb-6">"{t.quote}"</p>
                <div className="border-t border-slate-800/60 pt-4">
                  <p className="text-sm font-semibold text-slate-100">{t.name}</p>
                  <p className="text-xs text-slate-400">{t.title}</p>
                  <p className="text-xs text-slate-500">{t.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ FINAL CTA ═══════════════ */}
      <section className="py-24 relative overflow-hidden alt-grain" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)' }}>
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '32px 32px'}} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-blue-600/10 rounded-full blur-[120px] alt-float" />
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center relative">
          <div className="flex justify-center mb-6">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/40cc5c7f-c45c-491d-ad3f-cc7864e78796/images/4063fc24bf32f027848a8288e2bda515ed9b161b95609e2e01d5ded18984e549.jpeg"
              alt="Ari" className="alt-screen w-16 h-16 rounded-full object-cover ring-4 ring-blue-500/20"
            />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-50">Ready to make every dollar count?</h2>
          <p className="mt-4 text-base text-slate-400 max-w-xl mx-auto leading-relaxed">I'll help you connect with verified charities, automate employer matching, and track every cent. Start free, upgrade when you're ready.</p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              data-testid="cta-get-started-btn"
              onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }}
              className="group alt-shine flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold px-7 py-3.5 rounded-xl transition-all duration-200 text-sm shadow-lg hover:shadow-xl hover:shadow-blue-600/20"
            >
              Get started free
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <a
              href="#pricing"
              className="flex items-center gap-2 text-slate-400 hover:text-white font-semibold px-7 py-3.5 rounded-xl border border-slate-700 hover:border-slate-500 transition-all duration-200 text-sm"
            >
              Compare plans
            </a>
            <button
              data-testid="cta-book-demo-btn"
              onClick={() => setShowDemoModal(true)}
              className="flex items-center gap-2 text-slate-400 hover:text-white font-semibold px-7 py-3.5 rounded-xl border border-slate-700 hover:border-slate-500 transition-all duration-200 text-sm"
            >
              <CalendarDays className="w-4 h-4" />
              Book a demo
            </button>
          </div>
          <p className="mt-6 text-xs text-slate-600">No credit card required for free plan. Cancel paid plans anytime.</p>
        </div>
      </section>

      {/* ═══════════════ FOOTER ═══════════════ */}
      <footer className="py-12 border-t border-slate-800/60" style={{ background: '#07090E' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-10 mb-10">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <img src="/logo-mark.png" alt="Altrunic" className="w-7 h-7 object-contain" />
                <span className="text-base font-bold text-slate-50">Altrunic</span>
              </div>
              <p className="text-sm text-slate-500 leading-relaxed">The all-in-one platform for corporate social impact and ESG management.</p>
            </div>
            {[
              { title: 'Solutions', links: [
                { label: 'Corporate Giving', href: '/solutions/corporate-giving-platform' },
                { label: 'Donation Matching', href: '/solutions/employee-donation-matching' },
                { label: 'ESG Reporting', href: '/solutions/esg-reporting-software' },
                { label: 'For Nonprofits', href: '/solutions/nonprofit-donation-platform' },
              ]},
              { title: 'Product', links: [
                { label: 'Features', href: '#features' },
                { label: 'Pricing', href: '#pricing' },
                { label: 'Demo', href: '#demo' },
              ]},
              { title: 'Legal', links: [{ label: 'Privacy', href: '/privacy' }, { label: 'Terms', href: '/terms' }] },
            ].map((col, i) => (
              <div key={i}>
                <h4 className="text-xs font-semibold text-slate-400 tracking-wider uppercase mb-4">{col.title}</h4>
                <ul className="space-y-2.5">
                  {col.links.map((link, j) => (
                    <li key={j}><a href={link.href} className="text-sm text-slate-500 hover:text-slate-300 transition-colors duration-200">{link.label}</a></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-800/60 pt-8 text-center text-xs text-slate-600">
            <p>&copy; {new Date().getFullYear()} Altrunic. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* ═══════════════ AUTH MODAL ═══════════════ */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onMouseDown={(e) => e.target === e.currentTarget && setShowAuthModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto" data-testid="auth-modal">
            <div className="p-8">
              <div className="flex justify-between items-start mb-8">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {forgotPasswordMode 
                      ? (forgotStep === 1 ? 'Forgot password' : 'Reset password')
                      : authMode === 'login' ? 'Welcome back' : signupStep === 1 ? 'Create your account' : 'Company details'}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {forgotPasswordMode
                      ? (forgotStep === 1 ? 'Enter your email to receive a reset code' : 'Enter the code and your new password')
                      : authMode === 'login' ? 'Sign in to your dashboard' : signupStep === 1 ? 'Start your impact journey' : 'Tell us about your business'}
                  </p>
                </div>
                <button 
                  data-testid="auth-modal-close"
                  onClick={() => { setShowAuthModal(false); setSignupStep(1); setError(''); setForgotPasswordMode(false); setForgotStep(1); setForgotSuccess(''); }}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {error && (
                <div className="mb-6 p-3 bg-red-50 border border-red-100 text-red-600 rounded-lg text-sm" data-testid="auth-error">
                  {error}
                </div>
              )}

              {forgotSuccess && (
                <div className="mb-6 p-3 bg-blue-50 border border-blue-100 text-blue-700 rounded-lg text-sm" data-testid="forgot-success-msg">
                  {forgotSuccess}
                </div>
              )}

              {/* ── FORGOT PASSWORD FLOW ── */}
              {forgotPasswordMode ? (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  {forgotStep === 1 && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">Email address</label>
                      <input data-testid="forgot-email-input" type="email" placeholder="you@company.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors outline-none" required />
                    </div>
                  )}
                  {forgotStep === 2 && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">Reset code</label>
                        <input data-testid="reset-code-input" type="text" placeholder="Enter 6-digit code" value={resetCode} onChange={(e) => setResetCode(e.target.value)} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors outline-none text-center tracking-widest font-mono text-lg" maxLength={6} required />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">New password</label>
                        <div className="relative">
                          <input data-testid="reset-new-password-input" type={showPassword ? "text" : "password"} placeholder="Min 8 chars, include a number" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full px-3.5 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors outline-none" required />
                          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                        </div>
                      </div>
                    </>
                  )}
                  <button data-testid="forgot-submit-btn" type="submit" disabled={loading} className="w-full bg-gray-900 hover:bg-gray-800 text-white py-2.5 rounded-lg font-semibold text-sm transition-all disabled:opacity-50">{loading ? 'Processing...' : forgotStep === 1 ? 'Send reset code' : 'Reset password'}</button>
                  <button type="button" data-testid="forgot-back-to-login" onClick={() => { setForgotPasswordMode(false); setForgotStep(1); setError(''); setForgotSuccess(''); setForgotEmail(''); setResetCode(''); setNewPassword(''); }} className="w-full text-gray-500 py-2 text-sm font-medium hover:text-gray-700">Back to sign in</button>
                </form>
              ) : (
                <>
                  {authMode === 'login' && QUICK_LOGINS.length > 0 && showQuickLogins && (
                    <div className="mb-5" data-testid="quick-login-panel">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="h-px flex-1 bg-gray-200" />
                        <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">One-click sign in</span>
                        <div className="h-px flex-1 bg-gray-200" />
                      </div>
                      <div className="space-y-2">
                        {QUICK_LOGINS.map(acct => (
                          <button
                            key={acct.email}
                            type="button"
                            disabled={loading}
                            onClick={() => quickLogin(acct.email, acct.password)}
                            data-testid={`quick-login-${acct.id}`}
                            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg border border-gray-200 hover:border-gray-900 hover:bg-gray-50 transition-all text-left disabled:opacity-50"
                          >
                            <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-white flex-shrink-0 ${acct.tone}`}>
                              <acct.icon className="w-4 h-4" />
                            </span>
                            <span className="flex-1 min-w-0">
                              <span className="block text-sm font-semibold text-gray-900">{acct.label}</span>
                              <span className="block text-xs text-gray-500 truncate">{acct.email}</span>
                            </span>
                            <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-3 mt-4">
                        <div className="h-px flex-1 bg-gray-200" />
                        <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">Or use a password</span>
                        <div className="h-px flex-1 bg-gray-200" />
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleEmailAuth} className="space-y-4">
                    {authMode === 'signup' && signupStep === 1 && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1.5">Full name</label>
                          <input data-testid="signup-name-input" type="text" placeholder="Jane Smith" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors outline-none" required />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1.5">Company name</label>
                          <input data-testid="signup-company-input" type="text" placeholder="Acme Inc." value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors outline-none" required />
                        </div>
                      </>
                    )}
                    {authMode === 'signup' && signupStep === 2 && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1.5">Industry</label>
                          <select data-testid="signup-industry-select" value={formData.industry} onChange={(e) => setFormData({ ...formData, industry: e.target.value })} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors outline-none" required>
                            <option value="">Select industry</option>
                            {INDUSTRIES.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1.5">Company size</label>
                          <select data-testid="signup-size-select" value={formData.company_size} onChange={(e) => setFormData({ ...formData, company_size: e.target.value })} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors outline-none" required>
                            <option value="">Select size</option>
                            {COMPANY_SIZES.map(size => <option key={size.value} value={size.value}>{size.label}</option>)}
                          </select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">City</label>
                            <input data-testid="signup-city-input" type="text" placeholder="New York" value={formData.headquarters_city} onChange={(e) => setFormData({ ...formData, headquarters_city: e.target.value })} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors outline-none" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">State</label>
                            <input data-testid="signup-state-input" type="text" placeholder="NY" value={formData.headquarters_state} onChange={(e) => setFormData({ ...formData, headquarters_state: e.target.value })} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors outline-none" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1.5">Company website</label>
                          <input data-testid="signup-website-input" type="text" placeholder="acme.com" value={formData.company_website} onChange={(e) => setFormData({ ...formData, company_website: e.target.value })} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors outline-none" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Your role</label>
                            <input data-testid="signup-jobtitle-input" type="text" placeholder="Head of CSR" value={formData.job_title} onChange={(e) => setFormData({ ...formData, job_title: e.target.value })} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors outline-none" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Phone <span className="text-gray-400 font-normal">(optional)</span></label>
                            <input data-testid="signup-phone-input" type="tel" placeholder="(555) 123-4567" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors outline-none" />
                          </div>
                        </div>
                      </>
                    )}
                    {(authMode === 'login' || (authMode === 'signup' && signupStep === 2)) && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1.5">Email</label>
                          <input data-testid="auth-email-input" type="email" placeholder="you@company.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors outline-none" required />
                          {authMode === 'signup' && formData.email && !isCompanyEmail(formData.email) && (
                            <p className="text-red-500 text-xs mt-1">Please use your company email</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1.5">Password</label>
                          <div className="relative">
                            <input data-testid="auth-password-input" type={showPassword ? "text" : "password"} placeholder="Enter password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="w-full px-3.5 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors outline-none" required />
                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="toggle-password-visibility">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                          </div>
                          {authMode === 'login' && (
                            <button type="button" data-testid="forgot-password-link" onClick={() => { setForgotPasswordMode(true); setError(''); setForgotEmail(formData.email); }} className="text-xs font-medium text-blue-600 hover:text-blue-800 mt-1.5 transition-colors">Forgot password?</button>
                          )}
                        </div>
                      </>
                    )}
                    {authMode === 'signup' && signupStep === 1 ? (
                      <button data-testid="signup-continue-btn" type="button" onClick={() => setSignupStep(2)} className="w-full bg-gray-900 hover:bg-gray-800 text-white py-2.5 rounded-lg font-semibold text-sm transition-all">Continue</button>
                    ) : (
                      <button data-testid="auth-submit-btn" type="submit" disabled={loading} className="w-full bg-gray-900 hover:bg-gray-800 text-white py-2.5 rounded-lg font-semibold text-sm transition-all disabled:opacity-50">{loading ? 'Processing...' : authMode === 'login' ? 'Sign in' : 'Create account'}</button>
                    )}
                  </form>
                  {authMode === 'signup' && signupStep === 2 && (
                    <button data-testid="signup-back-btn" onClick={() => setSignupStep(1)} className="w-full mt-3 text-gray-500 py-2 text-sm font-medium hover:text-gray-700">Back</button>
                  )}
                  <div className="mt-6 text-center">
                    <p className="text-sm text-gray-500">
                      {authMode === 'login' ? "Don't have an account? " : 'Already have an account? '}
                      <button data-testid="auth-toggle-mode" onClick={() => { setAuthMode(authMode === 'login' ? 'signup' : 'login'); setSignupStep(1); setError(''); }} className="font-semibold text-gray-900 hover:underline">{authMode === 'login' ? 'Sign up' : 'Sign in'}</button>
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <BookDemoModal open={showDemoModal} onClose={() => setShowDemoModal(false)} />
      <ChatbotWidget context="sales" />
    </div>
  );
};

export default LandingPage;
